/**
 * StatCards - reusable row of metric cards with bottom-aligned numbers.
 *
 * Handles both simple (display-only) and interactive (clickable with active
 * state) cards. Labels are 12px in the second ink, readable for older users;
 * a clickable card is keyboard reachable and marks its active state with a
 * green left edge.
 */

import { cn } from '@/shared/lib/utils'

interface StatCard {
  key: string
  label: string
  value: string | number
  /** Optional one-line context under the number. */
  hint?: string
  /** When provided, card becomes clickable */
  onClick?: () => void
  /** Active highlight (left green border) */
  active?: boolean
}

interface StatCardsProps {
  cards: StatCard[]
}

export function StatCards({ cards }: StatCardsProps) {
  return (
    <div
      className={cn(
        'mb-6 grid grid-cols-2 gap-2.5 sm:grid-cols-3',
        cards.length >= 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4',
      )}
    >
      {cards.map((card) => {
        const isClickable = !!card.onClick
        return (
          <div
            key={card.key}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            aria-pressed={isClickable ? !!card.active : undefined}
            onClick={card.onClick}
            onKeyDown={
              isClickable
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      card.onClick?.()
                    }
                  }
                : undefined
            }
            className={cn(
              'flex flex-col justify-between rounded-lg bg-[var(--bg-subtle)] px-[18px] py-4',
              isClickable &&
                'cursor-pointer select-none border-l-2 transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-[var(--green)]',
              isClickable &&
                (card.active
                  ? 'border-l-[var(--green)]'
                  : 'border-l-transparent hover:border-l-[var(--green-border)]'),
            )}
          >
            <p className="mb-2 text-[12px] font-normal uppercase tracking-[0.12em] text-[var(--ink-2)]">
              {card.label}
            </p>
            <p className="font-display text-[34px] font-light leading-none text-[var(--ink)]">
              {card.value}
            </p>
            {card.hint && (
              <p className="mt-2 text-[12px] text-[var(--ink-2)]">{card.hint}</p>
            )}
          </div>
        )
      })}
    </div>
  )
}
